// Lookup de imagem para uma viagem.
//
// Camadas em ordem:
//   1. Cache em memória (Map por slug) — zero custo, vive enquanto a aba
//      está aberta.
//   2. Firestore `trip_images_cache/{slug}` — compartilhado entre todos os
//      usuários autenticados. Outra viagem com slug equivalente reusa.
//   3. /api/unsplash/fetch?q=<title>  — Pages Function autenticada que
//      bate no Unsplash. Resultado é gravado no Firestore pra próxima
//      viagem com slug igual já achar em cache.
//   4. **Fallback genérico**: pega 1 foto qualquer do `unsplash_cache`
//      (mesma coleção que landing/login usam, populada pelo seed). Garante
//      que cards sempre tenham UMA imagem decente — útil em dev local
//      sem wrangler (sem `/api/*`) e quando a API do Unsplash está fora.
//
// Falhas em qualquer camada caem pra próxima; falha de tudo retorna null
// (front renderiza fallback gradiente).
//
// Observação: `trip_images_cache` é compartilhado entre todos os users —
// se outra viagem do app usa "eurotrip", a sua "Eurotrip" reusa as mesmas
// 3 fotos (sem chamar API). É de propósito.

import {
  doc, getDoc, setDoc, serverTimestamp,
  collection, getDocs,
} from 'firebase/firestore';
import { db, auth } from '../../core/firebase.js';
import { slugify } from './slug.js';

const COLLECTION = 'trip_images_cache';
const FALLBACK_COLLECTION = 'unsplash_cache';
const PER_PAGE = 3;
const DAY_PER_PAGE = 5;     // pool de fotos por dia do roteiro (capa)

/** Cache em memória (não persistente). slug → photo escolhida no run. */
const _memCache = new Map();
/** Cache em memória das ESCOLHAS por DIA — chave "slug::dayOffset" → photo.
 *  Pinada na sessão (mesmo dia + mesmo slug = mesma foto enquanto a aba viva).
 *  Reload sorteia outra (opção B). */
const _dayMemCache = new Map();
/** Offset aleatório por slug, gerado uma vez por sessão. Usado pra rotacionar
 *  qual foto cada dia pega quando vários dias compartilham o mesmo slug
 *  (3 dias em Roma → 3 fotos diferentes do pool). Reload muda o offset. */
const _sessionOffsetBySlug = new Map();
/** Pool de fallback (todas as fotos do unsplash_cache da landing/login). */
let _fallbackPoolPromise = null;

/**
 * Retorna 1 foto (objeto com url, photographer, etc) para a viagem
 * representada por `title`. Pode retornar null se nada disponível.
 *
 * Quando `pinPhoto` é true (default), a mesma foto é retornada em
 * chamadas subsequentes pra mesma viagem dentro da sessão — cards não
 * trocam de imagem em re-renders.
 */
export async function getTripImage(title, { pinPhoto = true } = {}) {
  if (!title) return null;
  const slug = slugify(title);
  if (!slug) return null;

  if (pinPhoto && _memCache.has(slug)) return _memCache.get(slug);

  // 1. Firestore lookup
  const cached = await readCache(slug);
  if (cached && cached.length) {
    // ESCOLHA DETERMINÍSTICA (não random): mesma viagem (slug) sempre pega
    // a mesma foto entre as cacheadas. Sem isso, cada reload mostraria foto
    // diferente mesmo com cache hit.
    const pick = pickDeterministic(cached, slug);
    if (pinPhoto) _memCache.set(slug, pick);
    console.debug(`[images] cache HIT for slug="${slug}" (${cached.length} photo${cached.length === 1 ? '' : 's'} disponíveis)`);
    return pick;
  }

  // 2. Cache miss — chama API via Pages Function
  console.debug(`[images] cache MISS for slug="${slug}" → /api/unsplash/fetch`);
  const photos = await fetchFromApi(title);
  if (photos && photos.length) {
    // Grava no cache compartilhado (best-effort, não bloqueia retorno)
    writeCache(slug, title, photos)
      .then(() => console.debug(`[images] cached ${photos.length} photo${photos.length === 1 ? '' : 's'} for slug="${slug}"`))
      .catch((err) => console.warn('[images] cache write failed for', slug, err.message));
    const pick = pickDeterministic(photos, slug);
    if (pinPhoto) _memCache.set(slug, pick);
    return pick;
  }

  // 3. Fallback FILTRADO (determinístico via hash do slug — não muda em reload).
  console.debug(`[images] cache+API miss for slug="${slug}" → fallback do unsplash_cache`);
  const fallback = await pickFromFallbackPool(slug, title);
  if (pinPhoto) _memCache.set(slug, fallback);
  return fallback;
}

/**
 * Versão batch — pega imagens pra várias viagens em paralelo.
 * Retorna Map<title, photo|null>. Útil pra renderizar listas.
 */
export async function getTripImagesBatch(titles, opts) {
  const uniq = Array.from(new Set(titles.filter(Boolean)));
  const entries = await Promise.all(
    uniq.map(async (t) => [t, await getTripImage(t, opts).catch(() => null)])
  );
  return new Map(entries);
}

/**
 * Retorna 1 foto pra um DIA do roteiro (capa).
 *
 * Args:
 *   key       — query de busca (ex: "Roma Italy"). Slugify dela é a chave
 *               do doc de cache no Firestore (compartilhado entre dias com
 *               mesma cidade).
 *   dayOffset — posição do dia na viagem (0..N-1). Usado pra rotacionar
 *               qual foto do pool cada dia pega:
 *                 photo[(sessionOffset + dayOffset) % pool.length]
 *               Resultado: 3 dias em Roma com pool de 5 fotos pegam fotos
 *               nos índices, p.ex., (offset+2)%5, (offset+3)%5, (offset+4)%5
 *               — todos diferentes. Reload muda o offset → todos rotacionam.
 *
 * Pool size: 5 fotos (`DAY_PER_PAGE`). Pinado em `_dayMemCache` por
 * `slug::dayOffset` na sessão (sem flicker em re-renders), reload sorteia.
 */
export async function getDayImage(key, dayOffset = 0) {
  if (!key) return null;
  const slug = slugify(key);
  if (!slug) return null;

  const memKey = `${slug}::${dayOffset}`;
  if (_dayMemCache.has(memKey)) return _dayMemCache.get(memKey);

  // 1. Firestore lookup
  const cached = await readCache(slug);
  if (cached && cached.length) {
    const pick = pickFromPool(cached, slug, dayOffset);
    _dayMemCache.set(memKey, pick);
    return pick;
  }

  // 2. Cache miss — busca 5 fotos novas
  const photos = await fetchFromApi(key, DAY_PER_PAGE);
  if (photos && photos.length) {
    writeCache(slug, key, photos)
      .then(() => console.debug(`[images/day] cached ${photos.length} photos for slug="${slug}"`))
      .catch((err) => console.warn('[images/day] cache write failed:', err.message));
    const pick = pickFromPool(photos, slug, dayOffset);
    _dayMemCache.set(memKey, pick);
    return pick;
  }

  // 3. Fallback pool (compartilhado da landing/login)
  const fallback = await pickFromFallbackPool(slug, key);
  _dayMemCache.set(memKey, fallback);
  return fallback;
}

/**
 * Foto pra capa do CARD de hotel. Diferenças do getDayImage:
 *   - Pool de 3 fotos (vs 5 do dia) — menor variedade necessária
 *   - Pick determinístico via slug (mesmo hotel sempre mesma foto entre
 *     reloads — UX mais previsível pra um element específico)
 *
 * Query típica: `${hotel.name} ${cidade}`. Se hotel.name não rende bons
 * resultados, ainda assim a cidade dá contexto visual.
 */
export async function getHotelImage(query) {
  if (!query) return null;
  const slug = slugify(query);
  if (!slug) return null;

  // Mem cache (mesmo do getTripImage — chave é o slug)
  if (_memCache.has(slug)) return _memCache.get(slug);

  const cached = await readCache(slug);
  if (cached && cached.length) {
    const pick = pickDeterministic(cached, slug);
    _memCache.set(slug, pick);
    return pick;
  }

  const photos = await fetchFromApi(query, 3);
  if (photos && photos.length) {
    writeCache(slug, query, photos)
      .catch((err) => console.warn('[images/hotel] cache write failed:', err.message));
    const pick = pickDeterministic(photos, slug);
    _memCache.set(slug, pick);
    return pick;
  }

  // Fallback pool — compartilhado
  const fallback = await pickFromFallbackPool(slug, query);
  _memCache.set(slug, fallback);
  return fallback;
}

// Escolhe foto do pool combinando offset aleatório por slug (sessão) com o
// offset do dia. Garante que dias diferentes na mesma cidade recebam fotos
// diferentes, e que reload da página rotacione todos juntos.
function pickFromPool(pool, slug, dayOffset) {
  if (!pool || !pool.length) return null;
  if (!_sessionOffsetBySlug.has(slug)) {
    _sessionOffsetBySlug.set(slug, Math.floor(Math.random() * pool.length));
  }
  const sessionOffset = _sessionOffsetBySlug.get(slug);
  const idx = (sessionOffset + dayOffset) % pool.length;
  return pool[idx];
}

// ---------- internals ----------

async function readCache(slug) {
  try {
    const snap = await getDoc(doc(db, COLLECTION, slug));
    if (!snap.exists()) return null;
    return snap.data().photos || [];
  } catch (err) {
    console.warn('[images] cache read failed for', slug, err.message);
    return null;
  }
}

async function writeCache(slug, query, photos) {
  await setDoc(doc(db, COLLECTION, slug), {
    slug,
    query,
    photos,
    fetchedAt: serverTimestamp(),
  });
  // Unsplash API Terms exigem disparar o "download event" sempre que uma
  // foto é "usada" (cacheada/exibida). Fire-and-forget — falhas NÃO devem
  // bloquear o caching ou a renderização. Esse é o melhor ponto pra
  // chamar: roda uma única vez por foto (no momento que ela entra no
  // cache compartilhado). Reusos posteriores via readCache não disparam
  // de novo (correto: o "download" é por foto, não por exibição).
  for (const photo of photos) {
    if (photo?.downloadLocation) {
      fireUnsplashDownloadTrigger(photo.downloadLocation).catch(() => {});
    }
  }
}

/**
 * Dispara o download trigger pro Unsplash (compliance API Terms). É
 * fire-and-forget — qualquer falha é silenciosamente ignorada (não trava
 * UI nem cache). Chamada via Pages Function autenticada (proxy server-side
 * com a Client-ID — não expomos a chave no front).
 */
async function fireUnsplashDownloadTrigger(downloadLocation) {
  if (!downloadLocation) return;
  const user = auth.currentUser;
  if (!user) return;
  let idToken;
  try { idToken = await user.getIdToken(); }
  catch { return; }
  try {
    await fetch('/api/unsplash/download', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ downloadLocation }),
      keepalive: true,
    });
  } catch {
    // silent — Unsplash API Terms compliance is best-effort.
  }
}

async function fetchFromApi(query, perPage = PER_PAGE) {
  const user = auth.currentUser;
  if (!user) return null;
  let idToken;
  try { idToken = await user.getIdToken(); }
  catch { return null; }

  const url = `/api/unsplash/fetch?q=${encodeURIComponent(query)}&per_page=${perPage}`;
  let res;
  try {
    res = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
  } catch (err) {
    console.warn('[images] api fetch failed:', err.message);
    return null;
  }
  if (!res.ok) {
    console.warn('[images] api returned', res.status, 'for', query);
    return null;
  }
  let payload;
  try { payload = await res.json(); }
  catch { return null; }
  return payload.photos || [];
}

function pickRandom(arr) {
  if (!arr || !arr.length) return null;
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Escolha determinística de 1 item de um array, baseada num key (string).
 * Mesma key → mesmo item. Garante que a foto da viagem não muda entre
 * reloads (mesmo quando o mem-cache foi resetado).
 */
function pickDeterministic(arr, key) {
  if (!arr || !arr.length) return null;
  const idx = hashCode(String(key || '')) % arr.length;
  return arr[idx];
}

/**
 * Fallback determinístico filtrado por palavra-chave.
 *
 * Estratégia:
 *   1. Carrega o pool completo do `unsplash_cache` (categorias seedadas:
 *      europa, caribe, brasil, japao, savana).
 *   2. Filtra fotos cuja `query`, `location`, `altDescription` ou
 *      `description` compartilhem alguma palavra-chave com o slug/title
 *      da viagem (substring de pelo menos 4 chars).
 *   3. Se houver matches, escolhe uma DETERMINISTICAMENTE via hash do
 *      slug — assim a mesma viagem sempre pega a mesma foto-fallback.
 *   4. Se nada matchar, mantém o fallback genérico (pool inteiro com
 *      hash determinístico) — última cartada visual.
 *
 * Pra "Eurotrip" → tokens=[eurotrip] + prefixos → bate em "europe" da
 * categoria "europa". Pra "Porto de Galinhas" → bate em "brasil"/
 * "brazil"/"galinhas". Pra título arbitrário → fallback genérico.
 */
async function pickFromFallbackPool(slug, title) {
  if (!_fallbackPoolPromise) {
    _fallbackPoolPromise = (async () => {
      try {
        const snap = await getDocs(collection(db, FALLBACK_COLLECTION));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (err) {
        console.warn('[images] fallback pool fetch failed:', err.message);
        return [];
      }
    })();
  }
  const pool = await _fallbackPoolPromise;
  if (!pool.length) return null;

  const tokens = extractTokens(slug, title);
  const matches = tokens.length ? pool.filter((p) => photoMatchesTokens(p, tokens)) : [];

  const finalPool = matches.length ? matches : pool;
  const idx = hashCode(slug) % finalPool.length;
  return finalPool[idx];
}

/**
 * Extrai tokens significativos do slug+title pra matching com fotos cacheadas.
 * Inclui o token original (ex: "eurotrip") + variantes de prefixo de 4 chars
 * (ex: "euro") pra captar matches por substring (ex: "europe").
 */
function extractTokens(slug, title) {
  const fromSlug = (slug || '').split('-').filter((w) => w.length >= 3);
  const fromTitle = String(title || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 3);
  const tokens = new Set([...fromSlug, ...fromTitle]);

  // Adiciona prefixos de 4 chars (eurotrip → euro, portugal → port, etc).
  // Pra matchar fotos cacheadas em inglês: query="europe travel" ↔ "euro".
  for (const t of Array.from(tokens)) {
    if (t.length > 4) tokens.add(t.slice(0, 4));
  }
  return Array.from(tokens);
}

function photoMatchesTokens(photo, tokens) {
  const haystack = [
    photo.query, photo.location, photo.altDescription, photo.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return tokens.some((t) => haystack.includes(t));
}

function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ────────────────────────────────────────────────────────────────────────────
// Atribuição Unsplash — helpers centralizados.
//
// Os helpers `withUtm` e `buildCreditHtml` viviam duplicados em vários lugares
// (landing/unsplash.js, map/dayRail.js, images/coverImageModal.js, ...). Eles
// vivem AQUI agora como source-of-truth e devem ser importados pelos
// renderers. Consolidação completa proposta separadamente.
//
// Per Unsplash attribution guidelines:
//   - Photographer name + link com `?utm_source=mapiare&utm_medium=referral`
//   - Link pra https://unsplash.com com a mesma UTM
//   - Hotlink (não rehospedar) — já cumprido pelo `<img src=photo.url>`
//   - Download trigger — disparado em writeCache() acima.
// ────────────────────────────────────────────────────────────────────────────

const UNSPLASH_UTM = '?utm_source=mapiare&utm_medium=referral';

/** Anexa UTM Unsplash a uma URL (idempotente). */
export function withUtm(url) {
  if (!url) return '';
  if (url.includes('utm_source=')) return url;
  return url + (url.includes('?') ? UNSPLASH_UTM.replace('?', '&') : UNSPLASH_UTM);
}

function _escAttr(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * HTML do caption de crédito Unsplash. Aceita um photo object (shape do
 * /api/unsplash/fetch ou do cache do Firestore) com pelo menos
 * `photographer` e idealmente `photographerUrl`/`location`.
 *
 * Retorna só o INNER HTML — caller decide o wrapper/posicionamento.
 */
export function buildCreditHtml(photo) {
  if (!photo) return '';
  const name = _escAttr(photo.photographer || 'autor');
  const profileUrl = withUtm(photo.photographerUrl || '');
  const nameHtml = profileUrl
    ? `<a href="${_escAttr(profileUrl)}" target="_blank" rel="noopener noreferrer">${name}</a>`
    : name;
  const locationPart = photo.location ? `${_escAttr(photo.location)} · ` : '';
  return `${locationPart}foto de ${nameHtml} no <a href="https://unsplash.com${UNSPLASH_UTM}" target="_blank" rel="noopener noreferrer">Unsplash</a>`;
}

/**
 * Aplica o caption de crédito num container DOM. Cria (ou reusa) um
 * `<span class="<className>">` dentro do container e popula com o HTML
 * via `buildCreditHtml`. Idempotente (re-render limpo). Se a foto for null/
 * sem photographer, remove qualquer caption antigo.
 *
 * `className` default = 'unsplash-credit' — features novas usam esse e
 * estilizam na própria CSS. Features com CSS legada (ex.: map day rail
 * usa `.map-day-rail-cover-credit`) podem passar a classe própria pra
 * manter o estilo existente sem migrar CSS.
 */
export function applyUnsplashCredit(containerEl, photo, { className = 'unsplash-credit' } = {}) {
  if (!containerEl) return;
  // Procura por QUALQUER caption (default ou custom) pra remover antes
  // de criar — evita acúmulo se a className mudar entre chamadas.
  const existing = containerEl.querySelectorAll(':scope > .unsplash-credit, :scope > .map-day-rail-cover-credit');
  existing.forEach((el) => el.remove());
  // Só mostra crédito pra foto do Unsplash. Capa enviada pelo usuário
  // (source === 'upload') NÃO tem crédito — e como já removemos o caption
  // acima, trocar uma capa do Unsplash por uma enviada limpa o crédito antigo.
  if (!photo?.photographer || photo?.source === 'upload') return;
  const credit = document.createElement('span');
  credit.className = className;
  credit.innerHTML = buildCreditHtml(photo);
  containerEl.appendChild(credit);
}
